import React, { useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useAppSelector } from '../../../../app/store';
import { useCancelEncounterMutation } from '../../../../services/EncounterService';

interface CancelEncounterButtonProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

const CancelEncounterButton: React.FC<CancelEncounterButtonProps> = ({ onSuccess, onError }) => {
  const encounter = useAppSelector((state) => state.encounter.currentEncounter);
  const [cancelEncounter, { isLoading }] = useCancelEncounterMutation();
  const [open, setOpen] = useState(false);

  // Check if encounter is cancelled - don't show the button if already cancelled
  const isEncounterCancelled = encounter?.status === 'CANCEL';

  const handleCancel = async () => {
    if (!encounter) {
      onError?.('No encounter data available');
      return;
    }

    if (!encounter.id) {
      onError?.('Encounter ID is required for cancellation');
      return;
    }

    try {
      await cancelEncounter({ encounterId: encounter.id }).unwrap();
      onSuccess?.();
      setOpen(false);
    } catch (err: any) {
      const errorMessage = err?.data?.message || 'Failed to cancel encounter.';
      onError?.(errorMessage);
      setOpen(false);
    }
  };

  // Don't render the button if the encounter is already cancelled
  if (isEncounterCancelled) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        onClick={() => setOpen(true)}
        disabled={isLoading || !encounter}
        sx={{
          fontWeight: 600,
          px: 3,
          py: 1,
          borderColor: '#d32f2f',
          color: '#d32f2f',
          '&:hover': {
            borderColor: '#b71c1c',
            backgroundColor: 'rgba(211, 47, 47, 0.04)'
          }
        }}
      >
        Cancel Encounter
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Cancel Encounter</DialogTitle>
        <DialogContent>
          Are you sure you want to cancel this encounter? This action will set the encounter status to Cancelled and cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isLoading}>
            No, Keep Encounter
          </Button>
          <Button
            onClick={handleCancel}
            color="error"
            variant="contained"
            disabled={isLoading}
            sx={{ fontWeight: 600 }}
          >
            {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Yes, Cancel Encounter'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CancelEncounterButton;
